import { SizeDetails } from 'components/canvas/constants';
import {
  CrystalDrawing,
  DrawingKey,
  DrawingType,
  DrawingsType,
  SelectedDrawingDetails,
} from 'components/well/drawingManager/constants';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { useCallback, useMemo } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  DrawingsData,
  StoreImage,
  WellImagingDetails,
  getWellImageStoreImageByKey,
  setIsCreatingDrawing,
  setShowFrapGraph,
  updateWellDropImageTuningValues,
} from 'redux/reducers/wellReducer';
import {
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
  useAllRegions,
} from './experiment';
import {
  FetchImageWellDropMetaDataParams,
  fetchWellDropImageMetaDataThunk,
} from 'redux/thunks/well';
import { CaptureResultTuningValuesDTO, IWellImageDTO } from 'business/models/Models';
import {
  DetailTextProps,
  TFocusLevelImage,
  WellDetailsStates,
} from 'business/models/CustomModels';
import {
  getWellDropImageUniqueKeyByWellDetails,
  getWellDropImageUniqueKeyByWellNavigationDetails,
  getWellImagingDetailsKey,
} from 'redux/utils/well';
import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { setDropImageForKey } from 'redux/reducers/experimentReducer';

export const useWellDrawings = (
  plateId: number,
  wellNumber: number,
  dropNumber: number,
  regionName: string,
): {
  status: EFetchingStatus;
  drawings: DrawingsData;
  canvasSize: SizeDetails;
} => {
  return useRMSelector(
    (state) =>
      state.well.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
        regionName
      ],
  );
};

export const useSelectedWellDrawing = (): SelectedDrawingDetails | null => {
  const selectedDrawingDetails = useRMSelector((state) => {
    const { plateId, wellNumber, dropNumber, drawingId, drawingType, regionName } =
      state.well.selectedDrawing || {};

    const drawing = (
      state.well?.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
        regionName
      ]?.drawings?.[DrawingKey[drawingType]] as DrawingsType[]
    )?.find((drawing) => drawing.id === drawingId);

    return {
      selectedDrawingDetails: state.well.selectedDrawing,
      drawing: drawing || null,
    };
  });

  return useMemo(() => {
    if (selectedDrawingDetails.selectedDrawingDetails) {
      return {
        ...selectedDrawingDetails.selectedDrawingDetails,
        drawing: selectedDrawingDetails.drawing,
      };
    }
  }, [
    selectedDrawingDetails?.selectedDrawingDetails?.drawingId,
    selectedDrawingDetails?.selectedDrawingDetails?.drawingType,
    selectedDrawingDetails?.drawing,
  ]);
};

export const useSelectedDrawingTool = () =>
  useRMSelector((state) => state.well.selectedDrawingTool);

export const useAllSelectedWellDrawingsByType = (
  plateId: number,
  wellNumber: number,
  dropNumber: number,
  regionName: string,
  drawingType: DrawingType,
) => {
  const drawings = useWellDrawings(plateId, wellNumber, dropNumber, regionName);
  return drawings?.drawings?.[DrawingKey[drawingType]];
};

export const useWellDropImageData = (
  pid: number,
  wellNumber: number,
  dropNumber: number,
  imageBatchId: number,
  cpId: number,
) => {
  return useRMSelector(
    (state) =>
      state?.well?.wellDrawing?.[pid]?.[wellNumber]?.[dropNumber]?.images?.[
        imageBatchId
      ]?.[cpId],
  );
};

export const useWellDrawingFontsPreferences = () => {
  return useRMSelector((state) => state?.well?.drawingFontsPreferences);
};

export const useDrawingColor = () => {
  return useRMSelector((state) => state.well.drawingColor);
};

export const usePoiDrawingColor = () => {
  return useRMSelector((state) => state.well.poiColor);
};
export const useIsWellImagingEnabled = (
  plateId: number,
  wellNumber: number,
  dropNumber: number,
): boolean => {
  return useRMSelector(
    (state) =>
      state.well.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.isImagingEnabled,
  );
};

export const useWellImagingDetails = (
  plateId: number,
  wellNumber: number,
  dropNumber: number,
  imageBatchId: number,
  cpId: number,
  regionId: number,
): WellImagingDetails => {
  const imagingDetailsKey = getWellImagingDetailsKey(imageBatchId, cpId, regionId);
  return useRMSelector(
    (state) =>
      state.well.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.imagingDetails?.[
        imagingDetailsKey
      ],
  );
};

export const useImageStoreImageByWellParamsKey = (imageKey: string) => {
  const imageStore = useRMSelector((state) => state?.well?.imageStore);
  const fetchingStatus = imageStore[imageKey]?.fetchingStatus;

  return useMemo(() => {
    return [getWellImageStoreImageByKey(imageKey), fetchingStatus];
  }, [fetchingStatus]);
};

export const useImageStoreByWellParamsKey = (imageKey: string) => {
  const imageStore = useRMSelector((state) => state?.well?.imageStore);
  return imageStore?.[imageKey];
};

export const useCrystalDrawings = (
  plateId: number,
  wellNumber: number,
  dropNumber: number,
  regionName: string,
): CrystalDrawing[] => {
  const drawingsData = useWellDrawings(plateId, wellNumber, dropNumber, regionName);
  return drawingsData?.drawings?.crystals || [];
};

export const useShowOverrideTuning = () => {
  return useRMSelector((state) => state.well.showOverrideTuning);
};

export const useDropLocation = () => {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  return useRMSelector(
    (state) =>
      state.well.wellDrawing?.[wellNavigationDetails?.plateID]?.[
        wellNavigationDetails?.wellNum
      ]?.[selectedDropImageParameters?.dropNumber]?.drawingsDetails?.[
        selectedDropImageParameters?.regionName
      ]?.drawings?.dropLocations,
  );
};

export const useShowFrapGraph = (): [boolean, (value: boolean) => void] => {
  const dispatch = useRMDispatch();
  const showGraph = useRMSelector((state) => state.well.showFrapGraph);
  const setShowGraph = (value: boolean) => dispatch(setShowFrapGraph(value));
  return [showGraph, setShowGraph];
};

export const useWellDrawingStatus = (
  plateId: number,
  wellNumber: number,
  dropNumber: number,
  regionName: string,
): EFetchingStatus => {
  return useRMSelector(
    (state) =>
      state.well.wellDrawing?.[plateId]?.[wellNumber]?.[dropNumber]?.drawingsDetails?.[
        regionName
      ]?.status,
  );
};

export const useFocusLevelImagesAvailableKeys = () => {
  return useRMSelector((state) => state.well.FocusLevelImagesAvailableKeys);
};
export const useWellsIndependentZoom = () => {
  return useRMSelector((state) => state.well.wellsIndependentZoom);
};

export const useIsCreatingNewDrawing = (): {
  isCreatingNewDrawing: boolean;
  setIsCreatingNewDrawing: (value: boolean) => void;
} => {
  const dispatch = useRMDispatch();
  const isCreatingNewDrawing = useRMSelector((state) => state.well.isCreatingDrawing);
  const setIsCreatingNewDrawing = (value: boolean) => {
    dispatch(setIsCreatingDrawing(value));
  };
  return { isCreatingNewDrawing, setIsCreatingNewDrawing };
};

export const useFetchWellDropImageMetaData = () => {
  const dispatch = useRMDispatch();

  return (params: FetchImageWellDropMetaDataParams) =>
    dispatch(fetchWellDropImageMetaDataThunk(params));
};

export const useWellDropImageStoreStatus = () => {
  return useRMSelector((state) => state.well.imageStore);
};

const emptyObjectRef = {}; // so, we don't create new ref each time.
export const useCurrentWellDropImageStatus = (UIKey: string) => {
  return useRMSelector(
    (state) => state.well.imageStore[UIKey] || (emptyObjectRef as StoreImage),
  );
};

export const useIsCurrentWellImageLoaded = (wellDetails: WellDetailsStates): boolean => {
  const uKey = getWellDropImageUniqueKeyByWellDetails(wellDetails);
  const imgStatus = useCurrentWellDropImageStatus(uKey);

  return imgStatus?.fetchingStatus === EFetchingStatus.Loaded || false;
};

export const useIsAnyWellDropImageAvailable = (
  wellDetailsList: WellDetailsStates[],
): boolean => {
  const wellDropImageStore = useWellDropImageStoreStatus();

  return wellDetailsList.some((wellDetails) => {
    const uKey = getWellDropImageUniqueKeyByWellDetails(wellDetails);

    return wellDropImageStore[uKey]?.fetchingStatus === EFetchingStatus.Loaded || false;
  });
};

export const useWellDropImageMetaData = (): Record<string, IWellImageDTO> => {
  return useRMSelector((state) => state.well.wellDropImageMetaData.data);
};
export const useWellDropImageMetaDataStatus = (): EFetchingStatus => {
  return useRMSelector((state) => state.well.wellDropImageMetaData.status);
};

export const useUpdateWellDropImageTuningValues = () => {
  const dispatch = useRMDispatch();
  return (key: string, updatedTuningValues: ImageTuningValues) => {
    dispatch(updateWellDropImageTuningValues({ key, updatedTuningValues }));
  };
};

export const useWellDropImageTuningValues = (key: string): ImageTuningValues => {
  return useRMSelector(
    (state) => state.well.imageStore?.[key]?.meta?.updatedTuningValues,
  );
};

export const useWellDropMetaDataImageTuningValues = (
  key: string,
): CaptureResultTuningValuesDTO => {
  return useRMSelector(
    (state) => state.well.wellDropImageMetaData.data?.[key]?.captureResultTuningValuesDto,
  );
};

export function useFLDropImageByKey(key: string) {
  return useRMSelector((state) => state.plate.dropImageByKey?.[key]);
}

const useCurrentDropImageKey = (): string => {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allRegions = useAllRegions();
  return useMemo(
    () =>
      getWellDropImageUniqueKeyByWellNavigationDetails(
        wellNavigationDetails,
        selectedDropImageParameters,
        allRegions,
      ),
    [wellNavigationDetails, selectedDropImageParameters, allRegions],
  );
};

export const useSetCurrentDropImage = () => {
  const dispatch = useRMDispatch();
  const key = useCurrentDropImageKey();
  return useCallback(
    (image: TFocusLevelImage) => {
      dispatch(setDropImageForKey({ key, image }));
    },
    [dispatch, key],
  );
};

export const useCurrentDropImage = () => {
  const key = useCurrentDropImageKey();
  return useFLDropImageByKey(key);
};

export const useLastSelectedWellRect = (): DetailTextProps => {
  const navDetails = useLastSelectedWellNavigationDetails();
  const plateDto = useRMSelector((state) => state?.experiment?.expData?.plateDto);
  return useMemo(() => {
    if (!plateDto || !navDetails) {
      return;
    }

    const plate = plateDto?.find((x) => x.plateId === navDetails.plateID);
    const wellIndex = plate?.ingredients?.wellList?.findIndex(
      (x) => x.wellNumber === navDetails.wellNum,
    );

    return plate?.wellRects?.[wellIndex];
  }, [navDetails, plateDto]);
};
